<template lang="pug">
  .yz-search-btn(:class="searchBtnClasses")
    //- Search toggle button
    yz-btn(
      :size="size"
      icon-only
      :icon-color="iconColor"
      :icon="icon"
      :disabled="disabled"
      @click="toggleSearch"
      :class="{ 'yz-search-btn__toggle--active': showSearchField }"
    )

    //- Expandable search field
    .yz-search-btn__field(v-if="showSearchField || persistent" :class="fieldClasses")
      yz-text-field(
        ref="searchField"
        :value="internalValue"
        :placeholder="computedPlaceholder"
        :clearable="clearable"
        :disabled="disabled"
        :autofocus="autofocus"
        :size="size === 'xl' ? 'lg' : size"
        :class="fieldClass"
        :style="fieldStyle"
        @input="handleInput"
        @keydown.enter="handleSearch"
        @blur="handleBlur"
        @clear="handleClear"
      )
</template>

<script>
import { debounce } from '@/utils'

export default {
  name: 'YzSearchBtn',
  model: {
    prop: 'searchValue',
    event: 'input'
  },
  props: {
    // Button styling props
    size: {
      type: String,
      default: 'lg',
      validator: value => ['sm', 'md', 'lg', 'xl'].includes(value)
    },
    icon: {
      type: String,
      default: '$yzi-search-sm'
    },
    iconColor: {
      type: String,
      default: 'yz-neutral-500'
    },
    disabled: {
      type: Boolean,
      default: false
    },

    // Search functionality props
    searchValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: null
    },
    autoShow: {
      type: Boolean,
      default: false
    },
    persistent: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    autofocus: {
      type: Boolean,
      default: true
    },

    // Field styling props
    fieldClass: {
      type: String,
      default: null
    },
    fieldStyle: {
      type: Object,
      default: null
    },

    // Debounce configuration
    debounceDelay: {
      type: Number,
      default: 750
    }
  },
  data() {
    return {
      showSearchField: false,
      internalValue: this.searchValue
    }
  },
  computed: {
    searchBtnClasses() {
      return [
        'yz-search-btn',
        {
          'yz-search-btn--expanded': this.showSearchField,
          'yz-search-btn--persistent': this.persistent,
          'yz-search-btn--disabled': this.disabled
        }
      ]
    },
    fieldClasses() {
      return [
        'yz-search-btn__field',
        {
          'yz-search-btn__field--visible': this.showSearchField || this.persistent,
          'yz-search-btn__field--persistent': this.persistent
        }
      ]
    },
    computedPlaceholder() {
      if (this.placeholder) {
        return this.placeholder
      }
      // Fallback to i18n if available, otherwise use default
      return this.$t ? this.$t('type_to_search') : 'Type to search...'
    }
  },
  created() {
    this.debouncedSearch = debounce((value) => {
      this.$emit('search', value)
    }, this.debounceDelay)
  },
  watch: {
    searchValue: {
      immediate: true,
      handler(newValue) {
        this.internalValue = newValue
        // Show search field if there's a value (e.g., from URL query)
        if (newValue && !this.showSearchField && !this.persistent) {
          this.showSearchField = true
        }
      }
    },
    autoShow: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.showSearchField = true
          this.focusSearchField()
        }
      }
    }
  },
  methods: {
    toggleSearch() {
      if (this.disabled) return

      if (this.persistent) {
        // In persistent mode, just focus the field
        this.focusSearchField()
        return
      }

      this.showSearchField = !this.showSearchField
      this.$emit('toggle', this.showSearchField)

      if (this.showSearchField) {
        this.focusSearchField()
      } else {
        // Clear search when hiding field
        this.handleClear()
      }
    },

    focusSearchField() {
      if (this.autofocus) {
        this.$nextTick(() => {
          if (this.$refs.searchField && this.$refs.searchField.$refs && this.$refs.searchField.$refs.input) {
            this.$refs.searchField.$refs.input.focus()
          }
        })
      }
    },

    handleInput(value) {
      this.internalValue = value
      this.$emit('input', value)

      // Trigger debounced search on input
      this.debouncedSearch(value)
    },

    handleSearch() {
      this.$emit('search', this.internalValue)
    },

    handleClear() {
      this.internalValue = ''
      this.$emit('input', '')
      this.$emit('clear')
      this.$emit('search', '')
    },

    handleBlur() {
      this.$emit('blur')

      // Auto-hide if field is empty and not persistent
      if (!this.persistent && !this.internalValue?.trim()) {
        this.showSearchField = false
        this.$emit('toggle', false)
      }
    }
  }
}
</script>

<style scoped>
.yz-search-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.yz-search-btn__toggle--active {
  /* Add visual feedback when search is active */
  background-color: var(--yz-neutral-200) !important;
}

.yz-search-btn__field {
  opacity: 0;
  width: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.yz-search-btn__field--visible {
  opacity: 1;
  width: 200px;
}

.yz-search-btn__field--persistent {
  opacity: 1;
  width: 200px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .yz-search-btn__field--visible,
  .yz-search-btn__field--persistent {
    width: 150px;
  }
}

@media (max-width: 480px) {
  .yz-search-btn__field--visible,
  .yz-search-btn__field--persistent {
    width: 120px;
  }
}

/* Disabled state */
.yz-search-btn--disabled .yz-search-btn__field {
  opacity: 0.6;
  pointer-events: none;
}
</style>